import { useState, useCallback, useRef, useEffect } from "react";
import { Navbar } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Trash2, Save, X } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { MapContainer } from "react-leaflet/MapContainer";
import { TileLayer } from "react-leaflet/TileLayer";
import { Polygon } from "react-leaflet/Polygon";
import { Marker } from "react-leaflet/Marker";
import { useMap, useMapEvents } from "react-leaflet/hooks";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import * as turf from "@turf/helpers";
import turfArea from "@turf/area";
import { toast } from "sonner";

// Fix default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

interface MarkerData {
  id: number;
  lat: number;
  lng: number;
}

function createNumberedIcon(num: number) {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background: hsl(152, 45%, 28%);
      color: white;
      border-radius: 50%;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 700;
      border: 2px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    ">${num}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function MapClickHandler({ onMapClick }: { onMapClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/** Fits bounds exactly once, then removes itself */
function FitBoundsOnce({ markers }: { markers: MarkerData[] }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (!fitted.current && markers.length > 0) {
      fitted.current = true;
      const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    }
  }, [markers, map]);

  return null;
}

function DraggableMarker({
  marker,
  index,
  onDrag,
}: {
  marker: MarkerData;
  index: number;
  onDrag: (id: number, lat: number, lng: number) => void;
}) {
  const markerRef = useRef<L.Marker>(null);

  const eventHandlers = {
    dragend() {
      const m = markerRef.current;
      if (m) {
        const pos = m.getLatLng();
        onDrag(marker.id, pos.lat, pos.lng);
      }
    },
  };

  return (
    <Marker
      position={[marker.lat, marker.lng]}
      icon={createNumberedIcon(index + 1)}
      draggable
      ref={markerRef}
      eventHandlers={eventHandlers}
    />
  );
}

export default function AreaCalculatorMap() {
  const navigate = useNavigate();
  const { areaId } = useParams<{ areaId?: string }>();
  const { user } = useAuth();
  const [markers, setMarkers] = useState<MarkerData[]>([]);
  const [nextId, setNextId] = useState(1);
  const [woodType, setWoodType] = useState("");
  const [saving, setSaving] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load existing area if editing
  useEffect(() => {
    if (!areaId || !user || initialLoaded) return;

    const load = async () => {
      const { data, error } = await supabase
        .from("saved_areas")
        .select("*")
        .eq("id", areaId)
        .eq("user_id", user.id)
        .single();

      if (error || !data) {
        toast.error("Kunde inte ladda området.");
        navigate("/skogsverktyg/arearaknare");
        return;
      }

      const coords = data.coordinates as [number, number][];
      const loaded: MarkerData[] = coords.map((c, i) => ({
        id: i + 1,
        lat: c[1], // coordinates stored as [lng, lat]
        lng: c[0],
      }));
      setMarkers(loaded);
      setNextId(loaded.length + 1);
      setWoodType(data.wood_type || "");
      setInitialLoaded(true);
    };

    load();
  }, [areaId, user, initialLoaded, navigate]);

  // Mark initial load done for new areas
  useEffect(() => {
    if (!areaId) setInitialLoaded(true);
  }, [areaId]);

  const addMarker = useCallback(
    (lat: number, lng: number) => {
      setMarkers((prev) => [...prev, { id: nextId, lat, lng }]);
      setNextId((prev) => prev + 1);
    },
    [nextId]
  );

  const updateMarker = useCallback((id: number, lat: number, lng: number) => {
    setMarkers((prev) => prev.map((m) => (m.id === id ? { ...m, lat, lng } : m)));
  }, []);

  const removeMarker = useCallback((id: number) => {
    setMarkers((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const clearAll = () => {
    setMarkers([]);
    setNextId(1);
  };

  // Calculate area
  const positions = markers.map((m) => [m.lat, m.lng] as [number, number]);
  let areaSqm = 0;
  let areaHa = 0;
  if (markers.length >= 3) {
    const coords = markers.map((m) => [m.lng, m.lat] as [number, number]);
    coords.push(coords[0]);
    const polygon = turf.polygon([coords]);
    areaSqm = turfArea(polygon);
    areaHa = areaSqm / 10000;
  }

  // EXIF upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const exifr = (await import("exifr")).default;
    let added = 0;
    let skipped = 0;

    for (const file of Array.from(files)) {
      try {
        const gps = await exifr.gps(file);
        if (gps && gps.latitude && gps.longitude) {
          addMarker(gps.latitude, gps.longitude);
          added++;
        } else {
          skipped++;
        }
      } catch {
        skipped++;
      }
    }

    if (skipped > 0) {
      toast.warning("En eller flera bilder saknade GPS-data och kunde inte användas.");
    }
    if (added > 0) {
      toast.success(`${added} punkt${added > 1 ? "er" : ""} tillagd${added > 1 ? "a" : ""} från bilder.`);
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = async () => {
    if (!user) {
      toast.error("Du måste vara inloggad för att spara.");
      return;
    }
    if (markers.length < 3) return;

    setSaving(true);
    const coordinates = markers.map((m) => [m.lng, m.lat]);

    const payload = {
      user_id: user.id,
      coordinates: coordinates as any,
      area_sqm: Math.round(areaSqm * 100) / 100,
      area_ha: Math.round(areaHa * 10000) / 10000,
      wood_type: woodType || null,
    };

    let error;
    if (areaId) {
      // Update existing
      ({ error } = await supabase
        .from("saved_areas")
        .update(payload)
        .eq("id", areaId)
        .eq("user_id", user.id));
    } else {
      // Insert new
      ({ error } = await supabase.from("saved_areas").insert(payload));
    }

    setSaving(false);
    if (error) {
      toast.error("Kunde inte spara området.");
    } else {
      toast.success(areaId ? "Området har uppdaterats!" : "Området har sparats!");
      navigate("/skogsverktyg/arearaknare");
    }
  };

  return (
    <div className="flex h-screen flex-col">
      <Navbar />
      <div className="relative flex flex-1 flex-col">
        {/* Map */}
        <div className="flex-1">
          <MapContainer
            center={[62.0, 15.0]}
            zoom={5}
            style={{ height: "100%", width: "100%" }}
            className="z-0"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            <MapClickHandler onMapClick={addMarker} />

            {/* Only fit bounds once when editing an existing area */}
            {initialLoaded && markers.length > 0 && <FitBoundsOnce markers={markers} />}

            {markers.map((marker, i) => (
              <DraggableMarker
                key={marker.id}
                marker={marker}
                index={i}
                onDrag={updateMarker}
              />
            ))}

            {markers.length >= 3 && (
              <Polygon
                positions={positions}
                pathOptions={{
                  color: "hsl(152, 45%, 28%)",
                  fillColor: "hsl(152, 45%, 28%)",
                  fillOpacity: 0.25,
                  weight: 2,
                }}
              />
            )}
          </MapContainer>
        </div>

        {/* Back button */}
        <BackButton
          fallback="/skogsverktyg/arearaknare"
          className="absolute left-3 top-3 z-[1000] shadow-md"
        />

        {/* Bottom panel */}
        <div className="z-[1000] border-t border-border bg-card p-4 shadow-lg">
          {/* Marker list with delete buttons */}
          {markers.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {markers.map((m, i) => (
                <div
                  key={m.id}
                  className="flex items-center gap-1 rounded-full border border-border bg-secondary px-2 py-1 text-xs"
                >
                  <span className="font-medium">#{i + 1}</span>
                  <button
                    onClick={() => removeMarker(m.id)}
                    className="ml-1 text-destructive hover:text-destructive/80"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <p className="mb-2 text-sm font-medium">
            {markers.length} punkt{markers.length !== 1 ? "er" : ""} markerad{markers.length !== 1 ? "e" : ""}
          </p>

          {markers.length >= 3 && (
            <div className="mb-3 rounded-md bg-secondary p-3">
              <p className="text-lg font-bold text-primary">
                {Math.round(areaSqm).toLocaleString("sv-SE")} m²
              </p>
              <p className="text-sm text-muted-foreground">
                {areaHa.toFixed(2).replace(".", ",")} ha
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleImageUpload}
            />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              <Upload className="mr-1 h-4 w-4" /> Ladda upp bilder med GPS
            </Button>

            {markers.length > 0 && (
              <Button variant="outline" size="sm" onClick={clearAll}>
                <Trash2 className="mr-1 h-4 w-4" /> Rensa alla punkter
              </Button>
            )}
          </div>

          {markers.length >= 3 && (
            <div className="mt-4 space-y-3">
              <div>
                <Label className="text-xs">Trädslag (valfritt)</Label>
                <Select value={woodType} onValueChange={setWoodType}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Välj trädslag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Gran">Gran</SelectItem>
                    <SelectItem value="Tall">Tall</SelectItem>
                    <SelectItem value="Björk">Björk</SelectItem>
                    <SelectItem value="Ek">Ek</SelectItem>
                    <SelectItem value="Blandskog">Blandskog</SelectItem>
                    <SelectItem value="Övrigt">Övrigt</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button className="w-full" onClick={handleSave} disabled={saving}>
                <Save className="mr-2 h-4 w-4" />
                {saving ? "Sparar..." : areaId ? "Uppdatera område" : "Spara område"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
